import React, { useRef, useState } from 'react';
import { Upload, FileImage, X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatFileSize, useLibraryUpload } from '@/hooks/useLibraryUpload';

/**
 * Drop zone + upload queue for slideshow images. Every upload goes to the
 * facility's Image Library; where it goes after that is the caller's
 * `useLibraryUpload({ onUploaded })` (a slideshow adds them to itself).
 * Used by the Image Library tab and a slideshow's own page.
 */

const downloadTemplate = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 860;
  canvas.height = 200;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, 860, 200);
    const link = document.createElement('a');
    link.download = 'template-860x200.png';
    link.href = canvas.toDataURL();
    link.click();
  }
};

export function LibraryUploadPanel({
  upload,
  uploadVerb = 'Upload',
}: {
  upload: ReturnType<typeof useLibraryUpload>;
  /** Button wording, e.g. "Upload & add" on a slideshow page. */
  uploadVerb?: string;
}) {
  const { staged, uploading, limitLabel, stageFiles, removeStaged, clearStaged, uploadAll } = upload;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const pendingCount = staged.filter(s => s.status === 'pending').length;

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) stageFiles(e.target.files);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
    if (e.dataTransfer.files.length) stageFiles(e.dataTransfer.files);
  };

  return (
    <div>
      {/* Drop zone */}
      <div
        className={`rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
          dragActive
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-300 dark:border-gray-600'
        }`}
        onDragEnter={e => { e.preventDefault(); e.stopPropagation(); setDragActive(true); }}
        onDragLeave={e => { e.preventDefault(); e.stopPropagation(); setDragActive(false); }}
        onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={handleDrop}
      >
        <Upload className="mx-auto mb-3 h-10 w-10 text-gray-400" />
        <p className="mb-1 text-base font-medium text-gray-700 dark:text-gray-200">Drop files here</p>
        <p className="mb-4 text-sm text-gray-400">PNG · JPG · WebP · any dimensions{limitLabel ? ` · max ${limitLabel}` : ''}</p>

        {/* Dimension hint */}
        <div className="mb-4">
          <p className="mb-2 text-xs font-medium text-gray-600 dark:text-gray-400">Standard sponsor banner size:</p>
          <div
            className="mx-auto flex items-center justify-center border-2 border-dashed border-gray-400 bg-white text-xs text-gray-500 dark:border-gray-500 dark:bg-gray-700 dark:text-gray-400"
            style={{ width: '215px', height: '50px' }}
          >
            860 × 200 pixels
          </div>
          <p className="mt-1 text-xs text-gray-400">(shown at 25% scale)</p>
        </div>

        <div className="flex flex-col items-center gap-2">
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            Browse Files
          </Button>
          <Button variant="link" size="sm" onClick={downloadTemplate} className="h-auto p-0 text-xs">
            Download blank 860×200 template
          </Button>
        </div>

        <div className="mt-4 rounded border border-blue-100 bg-blue-50 p-3 text-left dark:border-blue-900 dark:bg-blue-900/20">
          <p className="mb-1 text-xs font-medium text-blue-800 dark:text-blue-300">How to use the template:</p>
          <ol className="space-y-0.5 text-xs text-blue-700 dark:text-blue-400">
            <li>1. Download the blank template above</li>
            <li>2. Open in Photoshop, Canva, or GIMP</li>
            <li>3. Add your logo and text</li>
            <li>4. Save as PNG and upload here</li>
          </ol>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/jpg,image/webp"
          className="hidden"
          onChange={handleFileInput}
        />
      </div>

      {/* Staged files */}
      {staged.length > 0 && (
        <div className="mt-6">
          <h3 className="mb-3 font-medium text-gray-800 dark:text-gray-200">
            Selected Files ({staged.length})
          </h3>
          <div className="space-y-2">
            {staged.map((entry, idx) => (
              <div key={idx} className="flex items-center justify-between rounded-lg border border-gray-200 p-3 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <FileImage className="h-5 w-5 flex-shrink-0 text-gray-400" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-gray-800 dark:text-gray-200">{entry.file.name}</p>
                    <p className="text-xs text-gray-400">{formatFileSize(entry.file.size)}</p>
                    {entry.status === 'error' && entry.error && (
                      <p className="text-xs text-red-600 dark:text-red-400">{entry.error}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  {(entry.status === 'pending' || entry.status === 'error') && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-gray-400 hover:text-gray-600"
                      onClick={() => removeStaged(idx)}
                      disabled={uploading}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  {entry.status === 'uploading' && (
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-600">
                        <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${entry.progress}%` }} />
                      </div>
                      <span className="text-xs text-gray-400">{entry.progress}%</span>
                      <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                    </div>
                  )}
                  {entry.status === 'success' && (
                    <span className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle className="h-4 w-4" />Uploaded
                    </span>
                  )}
                  {entry.status === 'error' && (
                    <span className="flex items-center gap-1 text-xs text-red-600">
                      <AlertCircle className="h-4 w-4" />Not uploaded
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {pendingCount > 0 && (
            <div className="mt-4 flex gap-2">
              <Button className="flex-1" onClick={uploadAll} disabled={uploading}>
                {uploading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Uploading…</>
                ) : (
                  <><Upload className="mr-2 h-4 w-4" />{uploadVerb} {pendingCount} File{pendingCount !== 1 ? 's' : ''}</>
                )}
              </Button>
              <Button variant="outline" onClick={clearStaged} disabled={uploading}>
                Clear All
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
